import { BookOpen, Layers, Users, HelpCircle } from "lucide-react";

import gettingStarted from "./getting-started";
import faq from "./faq";

import modules from "./modules";
import guides from "./guides";

// ---------------------------------------------------------------------------
// Section registry
// ---------------------------------------------------------------------------

export const sections = {
  "getting-started": {
    label: "Getting Started",
    icon: BookOpen,
    description:
      "Everything you need to understand Acadence and hit the ground running — platform overview, login, navigation, and role permissions.",
    pages: gettingStarted,
  },
  modules: {
    label: "Modules",
    icon: Layers,
    description:
      "In-depth documentation for each of the 21 Acadence modules, with step-by-step guides for every role.",
    pages: modules,
  },
  guides: {
    label: "Guides",
    icon: Users,
    description:
      "Practical how-to guides and workflows for common tasks across the platform.",
    pages: guides,
  },
  faq: {
    label: "FAQ",
    icon: HelpCircle,
    description:
      "Frequently asked questions covering general usage, technical details, and billing.",
    pages: faq,
  },
};

// ---------------------------------------------------------------------------
// Helper functions
// ---------------------------------------------------------------------------

/**
 * Returns a flat array of every page across all sections.
 * @returns {Array<Object>}
 */
export function getAllPages() {
  return Object.values(sections).flatMap((section) => section.pages);
}

/**
 * Returns the page object for a given section key and slug, or null if not found.
 * @param {string} sectionKey - e.g. "getting-started"
 * @param {string} slug       - e.g. "introduction"
 * @returns {Object|null}
 */
export function getPage(sectionKey, slug) {
  const section = sections[sectionKey];
  if (!section) return null;
  return section.pages.find((page) => page.slug === slug) ?? null;
}

/**
 * Returns the previous and next page objects within the same section,
 * relative to the given slug. Returns null for prev/next at the boundaries.
 * @param {string} sectionKey
 * @param {string} slug
 * @returns {{ prev: Object|null, next: Object|null }}
 */
export function getPrevNext(sectionKey, slug) {
  const section = sections[sectionKey];
  if (!section) return { prev: null, next: null };

  const pages = section.pages;
  const index = pages.findIndex((page) => page.slug === slug);

  if (index === -1) return { prev: null, next: null };

  return {
    prev: index > 0 ? pages[index - 1] : null,
    next: index < pages.length - 1 ? pages[index + 1] : null,
  };
}

/**
 * Returns the section object for a given section key, or null if not found.
 * @param {string} sectionKey - e.g. "faq"
 * @returns {Object|null}
 */
export function getSection(sectionKey) {
  return sections[sectionKey] ?? null;
}

// ---------------------------------------------------------------------------
// Default export
// ---------------------------------------------------------------------------

export default sections;
